import { LeadSystemAttributeSchemaName } from '@repo/crm-atoms';
import { type Node } from '@xyflow/react';
import { z } from 'zod';

import { Triggers } from '../automationConfig';
import { CommonNodeDataSchema } from './node';

export const TriggerNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.nativeEnum(Triggers),
});

export const LeadCreateNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.literal(Triggers.leadCreate),
  additionalOptions: z.object({
    criteriaGroupId: z.number().optional(),
    removableCriteriaGroupId: z.number().optional(),
  }).optional(),
});

export const LeadUpdateNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.literal(Triggers.leadUpdate),
  additionalOptions: z.object({
    criteriaGroupId: z.number().optional(),
    removableCriteriaGroupId: z.number().optional(),
    updateLead: z.object({
      comparator: z.enum(['and', 'or']),
      criterias: z.array(z.object({
        schemaName: z.string().min(1, 'Lead Field is required'),
        fromValue: z.string().optional(),
        toValue: z.union([z.string(), z.array(z.string())]).optional(),
      })).min(1, 'At least one field to update is required').refine((criterias) => {
        const ignoredFields = [LeadSystemAttributeSchemaName.owner, LeadSystemAttributeSchemaName.leadStage];

        const fieldsToCheck = criterias
          .filter((c) => !ignoredFields.includes(c.schemaName as LeadSystemAttributeSchemaName))
          .map((c) => c.schemaName);

        return new Set(fieldsToCheck).size === fieldsToCheck.length;
      }, { message: 'Error: Fields are repeated more than once' }),
    }),
  }).optional().refine((data) => data !== undefined, {
    message: 'Criteria must be added for node',
  }),
});

export const LeadActivityCreateNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.literal(Triggers.leadActivityCreate),
  additionalOptions: z.object({
    newActivity: z.object({
      comparator: z.literal('or'),
      criterias: z.array(
        z.object({
          activityId: z.number().int(),
        }),
      ).min(1, 'At least one activity is required')
        .refine((criterias) => criterias.every((criteria) => criteria.activityId !== -1), {
          message: 'Activity is required',
        }).refine((criterias) => {
          const activityIds = criterias.map((c) => c.activityId);
          return new Set(activityIds).size === activityIds.length;
        }, {
          message: 'Selected Activities must be unique',
        }),
    }),
    criteriaGroupId: z.number().int().optional(),
    removableCriteriaGroupId: z.number().int().optional(),
    runOncePerLead: z.boolean().optional(),
  }).optional().refine((data) => data !== undefined, {
    message: 'Criteria must be added for node',
  }),
});

export const LeadActivityUpdateNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.literal(Triggers.leadActivityUpdate),
  additionalOptions: z.object({
    updatedActivity: z.object({
      activityId: z.number().int().min(1, 'Activity is required'),
      activityAttributeId: z.number().int().min(1, 'Activity field is required'),
      fromValue: z.string().optional(),
      toValue: z.union([z.string(), z.array(z.string())]).optional(),
    }),
    criteriaGroupId: z.number().int().optional(),
    removableCriteriaGroupId: z.number().int().optional(),
  }).optional().refine((data) => data !== undefined, {
    message: 'Please configure which activity and field to monitor for updates',
  }),
});

export const TaskCreateNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.literal(Triggers.taskBaseCreate),
  additionalOptions: z.object({
    newTask: z.object({
      comparator: z.literal('or'),
      criterias: z.array(
        z.object({
          taskTemplateId: z.number().int(),
        }),
      ).min(1, 'At least one task template is required')
        .refine((criterias) => {
          const templateIds = criterias.map((c) => c.taskTemplateId);
          return new Set(templateIds).size === templateIds.length;
        }, { message: 'Selected task templates must be unique' }),
    }),
    criteriaGroupId: z.number().int().optional(),
    removableCriteriaGroupId: z.number().int().optional(),
  }).optional().refine((data) => data !== undefined, {
    message: 'Criteria must be added for node',
  }),
});

export const UserCreateNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.literal(Triggers.userCreate),
});

export const UserUpdateNodeDataSchema = CommonNodeDataSchema.extend({
  nodeSubType: z.literal(Triggers.userUpdate),
});

export type TriggerNodeData = z.infer<typeof TriggerNodeDataSchema>;
export type LeadCreateNodeData = z.infer<typeof LeadCreateNodeDataSchema>;
export type LeadUpdateNodeData = z.infer<typeof LeadUpdateNodeDataSchema>;
export type LeadActivityCreateNodeData = z.infer<typeof LeadActivityCreateNodeDataSchema>;
export type LeadActivityUpdateNodeData = z.infer<typeof LeadActivityUpdateNodeDataSchema>;
export type TaskCreateNodeData = z.infer<typeof TaskCreateNodeDataSchema>;
export type UserCreateNodeData = z.infer<typeof UserCreateNodeDataSchema>;
export type UserUpdateNodeData = z.infer<typeof UserUpdateNodeDataSchema>;

export type TriggerNodeType = Node<TriggerNodeData>;
export type LeadCreateNodeType = Node<LeadCreateNodeData>;
export type LeadUpdateNodeType = Node<LeadUpdateNodeData>;
export type LeadActivityCreateNodeType = Node<LeadActivityCreateNodeData>;
export type LeadActivityUpdateNodeType = Node<LeadActivityUpdateNodeData>;
export type TaskCreateNodeType = Node<TaskCreateNodeData>;
export type UserCreateNodeType = Node<UserCreateNodeData>;
export type UserUpdateNodeType = Node<UserUpdateNodeData>;
